/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { events } from '@adobe-commerce/event-bus';
import { fetchGraphQl } from '@/quote-management/api/';
import { CREATE_QUOTE_TEMPLATE_MUTATION } from './graphql/createQuoteTemplate';
import { state } from '@/quote-management/lib/state';
import { transformQuoteTemplate } from '@/quote-management/data/transforms';
import { NegotiableQuoteTemplateModel } from '@/quote-management/data/models/negotiable-quote-template-model';

export const createQuoteTemplate = async (
  quoteId: string
): Promise<NegotiableQuoteTemplateModel | null> => {
  if (!state.authenticated) {
    throw new Error('Unauthorized');
  }

  if (!quoteId) {
    throw new Error('Cart ID is required');
  }

  try {
    const results: any = await fetchGraphQl(CREATE_QUOTE_TEMPLATE_MUTATION, {
      variables: {
        cartId: quoteId,
      },
    });

    if (!results?.data?.requestNegotiableQuoteTemplateFromQuote) {
      throw new Error('Failed to create quote template');
    }

    const transformedQuoteTemplate = transformQuoteTemplate(
      results.data.requestNegotiableQuoteTemplateFromQuote
    );

    if (!transformedQuoteTemplate) {
      throw new Error('Failed to transform quote template data');
    }

    events.emit('quote-management/quote-template-data', {
      quoteTemplate: transformedQuoteTemplate,
      permissions: state.permissions,
    });

    return transformedQuoteTemplate;
  } catch (error) {
    return Promise.reject(error);
  }
};
